import {
  confTypeToPrefix,
  stripConferencePrefix,
  extractConfTypeFromPrefix,
  validateConferenceAccess,
} from '../utils/conference-type';

// Pure route/variant resolution logic, extracted verbatim from router/index.js
// so it can be exercised directly by unit tests without booting the router
// (which eagerly imports every page component plus Auth0/Capacitor).

export const EXCLUDED_GUARD_PATHS = [
  '/',
  '/access-denied',
  '/privacy-policy',
  '/terms-of-use',
  '/profile/pub/:id',
  '/pre-sign-in',
  '/download/student-guide',
  '/callback',
];

const CONF_PREFIXES = ['', '/gpcr', '/gic'];

export function isExcludedPath(path) {
  return EXCLUDED_GUARD_PATHS.includes(path);
}

export function buildRouteVariantMap(routes) {
  const map = {};
  for (const route of routes) {
    for (const prefix of CONF_PREFIXES) {
      if (route.path.startsWith(prefix + '/') || route.path === prefix) {
        const basePath = prefix ? route.path.slice(prefix.length) : route.path;
        if (!basePath) continue;
        if (!map[basePath]) map[basePath] = new Set();
        map[basePath].add(prefix);
      }
    }
  }
  return map;
}

export function resolveConferenceRoute(confType, conferences, toPath, routeVariantMap) {
  // 1. Strip any existing conference prefix to get base path
  const basePath = stripConferencePrefix(toPath);

  // 2. Check the variant map — how many conference versions does this page have?
  //
  // LOAD-BEARING QUIRK: toPath is a fullPath, so a query string makes this
  // lookup miss and the variant redirect silently no-op. The interpretation
  // deep link (/home?openInterpretation=..., issue #278) DEPENDS on this for
  // GPCR/GIC-mode users: gens Home must mount first to run its watcher, and
  // only the post-strip bare /home gets remapped. "Fixing" this lookup to
  // strip the query first would silently kill that feature for those users
  // (and note the remap return below drops query+hash anyway — pre-existing).
  // Pinned by "QUIRK: query string bypasses variant redirect" in
  // tests/unit/conference-resolution.spec.js.
  const variants = routeVariantMap[basePath];

  // No variants or only 1 variant → shared/unique page, no redirect needed
  if (!variants || variants.size <= 1) return '';

  // 3. Page has multiple conference versions — try the user's mode first
  const targetPrefix = confTypeToPrefix(confType);
  const targetPath = targetPrefix + basePath;

  if (variants.has(targetPrefix)) {
    // Conference version exists for user's mode — redirect if not already there
    return targetPath !== toPath ? targetPath : '';
  }

  // 4. No variant for user's mode — find the best fallback
  //    (a variant that exists AND user has access to)
  for (const prefix of variants) {
    const fallbackType = extractConfTypeFromPrefix(prefix);
    if (validateConferenceAccess(fallbackType, conferences)) {
      const fallbackPath = prefix + basePath;
      return fallbackPath !== toPath ? fallbackPath : '';
    }
  }

  // 5. No accessible variant at all — redirect to home
  const homePrefix = confTypeToPrefix(confType);
  return homePrefix ? `${homePrefix}/home` : '/home';
}
